/**
 * Ontario provincial and Toronto municipal land transfer tax (graduated tiers).
 * Used by the closing costs Canada article and regression tests.
 */

const ONTARIO_TIERS: { upTo: number; rate: number }[] = [
  { upTo: 55_000, rate: 0.005 },
  { upTo: 250_000, rate: 0.01 },
  { upTo: 400_000, rate: 0.015 },
  { upTo: Infinity, rate: 0.02 },
];

export function calculateOntarioLandTransferTax(purchasePrice: number): number {
  let tax = 0;
  let prev = 0;
  for (const tier of ONTARIO_TIERS) {
    const band = Math.min(purchasePrice, tier.upTo) - prev;
    if (band <= 0) break;
    tax += band * tier.rate;
    prev = tier.upTo;
    if (purchasePrice <= tier.upTo) break;
  }
  return Math.round(tax);
}

export const ONTARIO_FTHB_REBATE_CAP = 4_000;
export const TORONTO_FTHB_REBATE_CAP = 4_475;

export function netOntarioLtt(purchasePrice: number, firstTimeBuyer: boolean): number {
  const gross = calculateOntarioLandTransferTax(purchasePrice);
  const rebate = firstTimeBuyer ? Math.min(gross, ONTARIO_FTHB_REBATE_CAP) : 0;
  return gross - rebate;
}

export function netTorontoMltt(purchasePrice: number, firstTimeBuyer: boolean): number {
  const gross = calculateOntarioLandTransferTax(purchasePrice);
  const rebate = firstTimeBuyer ? Math.min(gross, TORONTO_FTHB_REBATE_CAP) : 0;
  return gross - rebate;
}

export interface ClosingCostEstimates {
  provincialLttGross: number;
  netProvincialLtt: number;
  municipalLttGross: number;
  netTorontoMltt: number;
  cmhcPremiumPst: number;
  legalFees: number;
  titleInsurance: number;
  homeInspection: number;
  closingAdjustments: number;
  totalBeyondDownPaymentFthb: number;
  totalBeyondDownPaymentRepeat: number;
}

export function ontarioClosingCostEstimates(opts: {
  purchasePrice: number;
  cmhcPremium: number;
  isToronto?: boolean;
  cmhcPstRate?: number;
  legalFees?: number;
  titleInsurance?: number;
  homeInspection?: number;
  closingAdjustments?: number;
}): ClosingCostEstimates {
  const {
    purchasePrice,
    cmhcPremium,
    isToronto = true,
    cmhcPstRate = 0.08,
    legalFees = 1750,
    titleInsurance = 325,
    homeInspection = 500,
    closingAdjustments = 1750,
  } = opts;

  const provincialLttGross = calculateOntarioLandTransferTax(purchasePrice);
  const municipalLttGross = isToronto ? provincialLttGross : 0;
  const netProvincialFthb = netOntarioLtt(purchasePrice, true);
  const netProvincialRepeat = netOntarioLtt(purchasePrice, false);
  const netMlttFthb = isToronto ? netTorontoMltt(purchasePrice, true) : 0;
  const netMlttRepeat = isToronto ? netTorontoMltt(purchasePrice, false) : 0;
  const cmhcPremiumPst = Math.round(cmhcPremium * cmhcPstRate);

  const fixedCash =
    cmhcPremiumPst + legalFees + titleInsurance + homeInspection + closingAdjustments;

  return {
    provincialLttGross,
    netProvincialLtt: netProvincialFthb,
    municipalLttGross,
    netTorontoMltt: netMlttFthb,
    cmhcPremiumPst,
    legalFees,
    titleInsurance,
    homeInspection,
    closingAdjustments,
    totalBeyondDownPaymentFthb: netProvincialFthb + netMlttFthb + fixedCash,
    totalBeyondDownPaymentRepeat: netProvincialRepeat + netMlttRepeat + fixedCash,
  };
}

/** Toronto dual LTT closing costs (provincial + municipal). */
export function torontoClosingCostEstimates(opts: {
  purchasePrice: number;
  downPayment: number;
  cmhcPremium: number;
  cmhcPstRate?: number;
  legalFees?: number;
  titleInsurance?: number;
  homeInspection?: number;
  closingAdjustments?: number;
}): ClosingCostEstimates {
  return ontarioClosingCostEstimates({ ...opts, isToronto: true });
}

/** Repeat-buyer net municipal LTT (no rebate). */
export function netTorontoMlttRepeat(purchasePrice: number): number {
  return calculateOntarioLandTransferTax(purchasePrice);
}
